import React from 'react';
import styled, { css } from 'styled-components';
import { GetColor, Icon } from 'venn-ui-kit';
import { CashIcon } from '../../../../common/CashIcon';

type NameWithMappingErrorRendererProps = {
  name: string | undefined;
  needsMapping: boolean;
  duplicateInvestment: boolean;
  isExcluded: boolean;
  shortHistory: boolean;
  isCashNode: boolean | undefined;
  cashBalance: number | undefined;
  needsAssigning: boolean | undefined;
};

export const NameWithMappingErrorRenderer = ({
  name,
  needsMapping,
  duplicateInvestment,
  isExcluded,
  shortHistory,
  isCashNode,
  cashBalance,
  needsAssigning,
}: NameWithMappingErrorRendererProps) => {
  return (
    <Wrapper isCompact>
      <span>
        {!isExcluded &&
          (needsMapping || needsAssigning || duplicateInvestment ? (
            <StyledIcon type="triangle-exclamation" />
          ) : shortHistory ? (
            <WarningIcon type="diamond-exclamation" />
          ) : null)}
        <Name isExcluded={isExcluded}>{name}</Name>
        {isCashNode && (
          <>
            {' '}
            <CashIcon isNegativeAssets={(cashBalance ?? 0) < 0} />
          </>
        )}
      </span>
      {!isExcluded && needsMapping && <ErrorSpan>Could not be mapped to an investment in the Data Library.</ErrorSpan>}
      {!isExcluded && needsAssigning && <ErrorSpan>No approved strategy could be found for this investment.</ErrorSpan>}
      {!isExcluded && duplicateInvestment && (
        <ErrorSpan>
          Duplicate investments are not allowed in the same strategy. Click {'\u22EF'} to exclude or merge.
        </ErrorSpan>
      )}
      {!isExcluded && shortHistory && (
        <SubText>
          Returns data for this investment does not cover all allocation dates. After upload, please add a proxy or
          additional returns data for this investment.
        </SubText>
      )}
    </Wrapper>
  );
};

const SubText = styled.span`
  font-size: 12px;
`;

const ErrorSpan = styled(SubText)`
  color: ${GetColor.Error};
`;

const StyledIcon = styled(Icon)`
  color: ${GetColor.Error};
  margin-right: 4px;
  font-size: 14px;
  font-weight: 900;
`;

const WarningIcon = styled(Icon)`
  color: ${GetColor.Orange};
  margin-right: 4px;
  font-size: 14px;
  font-weight: 900;
`;

const Name = styled.span<{ isExcluded: boolean }>`
  ${(props) =>
    props.isExcluded &&
    css`
      color: ${GetColor.GreyScale.Grey80};
      text-decoration: line-through;
    `};
`;

const Wrapper = styled.div<{ isCompact: boolean }>`
  height: 100%;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  ${(props) => props.isCompact && 'line-height: 16px;'};
`;
